// 🎸 LineupSection - Section de présentation du lineup artistique
// Affiche l'invité spécial et le répertoire des artistes joués
// Utilisé dans : src/pages/HomePage.jsx

import { Music, Disc, Award, Sparkles } from 'lucide-react'
import eventData from '@/locales/event.json'

/**
 * Composant affichant la programmation artistique du festival
 * Présente l'invité spécial avec mise en avant et la liste des artistes du répertoire
 * Les données sont chargées depuis le fichier JSON event.json
 */
export default function LineupSection() {
  const { lineup } = eventData

  return (
    <section className="section-padding bg-gradient-to-br from-neutral-900 via-neutral-950 to-black text-white">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-display text-primary-500 mb-4">
            {lineup.title}
          </h2>
          <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
            {lineup.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Invité spécial */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-500/20 rounded-full blur-2xl" />
            <div className="relative bg-gradient-to-br from-primary-950 to-neutral-900 rounded-2xl p-8 border-2 border-primary-500/30 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary-500 rounded-xl">
                  <Award className="text-white" size={28} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-primary-400 font-bold mb-1">
                    Invité Spécial
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white">
                    {lineup.specialGuest.name}
                  </h3>
                </div>
              </div>
              
              <p className="text-neutral-300 mb-4 leading-relaxed">
                {lineup.specialGuest.description}
              </p>
              
              <div className="bg-black/30 rounded-lg p-4 border border-primary-500/20">
                <div className="flex items-start gap-2">
                  <Sparkles className="text-primary-400 mt-1 flex-shrink-0" size={18} />
                  <p className="text-sm text-neutral-400">
                    {lineup.specialGuest.role}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Répertoire */}
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/20 rounded-full blur-2xl" />
            <div className="relative bg-gradient-to-br from-neutral-900 to-neutral-950 rounded-2xl p-8 border border-neutral-700 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                  <Disc className="text-primary-600 dark:text-primary-400" size={28} />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold text-white">
                    {lineup.repertoire.title}
                  </h3>
                  <p className="text-sm text-neutral-400">
                    {lineup.repertoire.description}
                  </p>
                </div>
              </div>

              {/* Grille d'artistes */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {lineup.repertoire.artists.map((artiste, index) => (
                  <div
                    key={index}
                    className="group bg-gradient-to-br from-primary-950/50 to-neutral-900/50 hover:from-primary-900 hover:to-neutral-800 rounded-lg p-4 border border-primary-500/20 hover:border-primary-500/50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <Music className="text-primary-400 group-hover:scale-110 transition-transform" size={20} />
                      <span className="font-bold text-white">{artiste}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-black/30 rounded-lg p-4 border border-neutral-700">
                <p className="text-sm text-neutral-400 italic">
                  {lineup.repertoire.note}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full">
            <Music className="text-primary-400" size={20} />
            <span className="text-neutral-200">
              Les plus grands classiques du rock en live !
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
